import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import "./styles/globals.scss";
import PageNotFound from "./pages/404";
import { useEffect, useState } from "react";
import {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import Loader from "./components/Loader";
import { PageLayout } from "./components/Layout";
import Login from "./pages/Login";
import SessionService from "./services/SessionService";
import AADLoginResponse from "./pages/AADLoginResponse";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "./services/ApiService";
import RolesList from "./pages/Roles/index";
import ProductList from "./pages/Products/ProductList";
import UserList from "./pages/Users";
import ProjectStageSetting from "./pages/ApplicationSetting/Project-Stage-Setting";
import AttachmentTypeSetting from "./pages/ApplicationSetting/AttachmentTypeSetting";
import MarketingDesignerSetting from "./pages/ApplicationSetting/MarketingDesignerSetting";
import RoyaltyDesignerSetting from "./pages/ApplicationSetting/RoyaltyDesignerSetting";
import SupplierSetting from "./pages/ApplicationSetting/SupplierSetting";
import ProductFinishSetting from "./pages/ApplicationSetting/ProductFinishSetting";
import PrimaryMaterialSetting from "./pages/ApplicationSetting/PrimaryMaterialSetting";
import HTSMaterialSetting from "./pages/ApplicationSetting/HTSMaterialSetting";
import HTSDetailSetting from "./pages/ApplicationSetting/HTSMaterialDetailSetting";
import StockCodeHierarchySetting from "./pages/ApplicationSetting/StockCodeHierarchySetting";
import BaseCodeHierarchySetting from "./pages/ApplicationSetting/BasecodeHierarchySetting";
import CategorySetting from "./pages/ApplicationSetting/CategorySetting";
import DataFieldTypeSetting from "./pages/ApplicationSetting/DataFieldTypeSetting";
import CreateProduct from "./pages/Products/CreateProduct";
import ManufactoryMethodSetting from "./pages/ApplicationSetting/ManufacturingMethodSetting";
import BulkUpload from "./pages/Products/BulkUpload";
import BulkUpdate from "./pages/Products/BulkUpdate";
import ProjectStatusSetting from "./pages/ApplicationSetting/ProjectStatusSetting";
import ProtectedRoutes from "./components/ProtectedRoutes";
import TagSetting from "./pages/ApplicationSetting/TagSetting";
import EditProduct from "./pages/Products/EditItem/EditProduct";
import { Permissions } from "./models/Enum";
import AttachmentList from "./pages/Report/ViewAttachment/AttachmentList";
import ExecutiveDashboard from "./pages/ExecutiveDashboard/ExecutiveDashboard";
import ItemfunctionSetting from "./pages/ApplicationSetting/ItemFunctionSetting";
import Assignment from "./pages/ApplicationSetting/Assignment";
import SecurityGroup from "./pages/ApplicationSetting/SecurityGroup";
import SkuTypeSetting from "./pages/ApplicationSetting/SkuTypeSetting";
import StyleSetting from "./pages/ApplicationSetting/StyleSetting";
import QuoteList from "./pages/Products/Quotes/QuoteList";
import ProjectDashboard from "./pages/ProjectDashboard/ProjectDashboard";
import BulkUploadAttachments from "./pages/Products/BulkUploadAttachments";
import { RootState, useAppDispatch, useAppSelector } from "./store";
import { disableLoader, enableLoader } from "./reducers/globalLoadingReducer";
import { EXCLUDED_ENDPOINTS_FOR_PREVENTING_GLOBAL_LOADER } from "./pages/Products/EditItem/Constants";
import UserDashboard from "./pages/UserDashboard/UserDashboard";
import BulkCreateQuote from "./pages/Products/Quotes/BulkCreateQuote";
import BulkUpdateQuote from "./pages/Products/Quotes/BulkUpdateQuote";
import CreateUpdateQuote from "./pages/Products/Quotes/CreateUpdateQuote";
import CreateUpdateTooling from "./pages/Products/Tooling/CreateUpdateTooling";
import ToolingList from "./pages/Products/Tooling/ToolingList";
import SeriesNameSetting from "./pages/ApplicationSetting/SeriesNameSetting";
import AssignmentList from "./pages/Products/AssignmentList";
import { StatusReports } from "./pages/Report/StatusReports/StatusReports";
import BulkCreateTooling from "./pages/Products/Tooling/BulkCreateTooling";
import BulkUpdateTooling from "./pages/Products/Tooling/BulkUpdateTooling";
import LeagoToPIMFieldList from "./pages/ApplicationSetting/LeagoToPIMFieldList";
import NotificationLogs from "./pages/Report/NotificationLogs/NotificationLogs";
import ExportProductsFieldSequence from "./pages/ApplicationSetting/ExportProductsFieldSequence";
import InactiveProductList from "./pages/Products/InactiveProductList";
import { updateNotificationLogRequest } from "./reducers/notificationLogsReducer";

function App() {
  const sessionService = SessionService.getInstance();
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionService.isLoggedIn()
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const defaultMessage =
    "Error response received from server. Please try again after refreshing your page";

  const globalLoader = useAppSelector((state: RootState) => state.globalLoading.loader);

  useEffect(() => {
    setIsAuthenticated(sessionService.isLoggedIn());
  }, [sessionService]);

  useEffect(() => {
    api.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {

        // Check if the API endpoint should be excluded from showing loader
        const shouldShowLoader = !EXCLUDED_ENDPOINTS_FOR_PREVENTING_GLOBAL_LOADER.some((endpoint) =>
          config.url?.includes(endpoint)
        );

        if (shouldShowLoader && !config?.headers?.disableLoader) {
          dispatch(enableLoader());
        }
        
        if (
          config.url &&
          !config.url.includes(process.env.REACT_APP_API_URL as string)
        ) {
          config.url = process.env.REACT_APP_API_URL + config.url;
        }

        const sessionInfo: any = JSON.parse(
          window?.localStorage.getItem("session") || "{}"
        );
        const key = process.env.REACT_APP_API_KEY;
        if (key) {
          config.headers["x-api-key"] = key;
        }

        if (sessionInfo.token) {
          config.headers.Authorization = `Bearer ${sessionInfo.token}`;
        }

        return config;
      },
      (err: any) => {
        dispatch(disableLoader());
        return Promise.reject(err);
      }
    );

    api.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        dispatch(disableLoader());
        return response;
      },
      (err: any) => {
        dispatch(disableLoader());

        if (err?.response?.status === 401) {
          sessionService.clearSession();
          sessionService.clearReduxState();
          navigate("/login");
        } else if (err?.response?.status === 404) {
          toast.error(err?.response?.data || err.message || "Could not find the requested resource");
        } else if(err?.response?.status === 400) {
          toast.error(err?.response?.data?.message || err?.response?.data || defaultMessage);
        } else if(err?.response?.status === 403) {
          toast.error("You lack the required permission to perform this action");
        } else {
          toast.error(() => (
            <span
              dangerouslySetInnerHTML={{ __html: getErrorDescription(err) }}
            />
          ));
        }

        return Promise.reject(err);
      }
    );
  }, [api.axiosInstance]);

  //Extracts error details from the API Response
  const getErrorDescription = (err: AxiosError): string => {
    if (err?.response?.data) {
      const errorData = err.response.data as any;
      if (typeof errorData === "object" && errorData.errors) {
        if (typeof errorData.errors === "object") {
          const errorsObjectKeys = Object.keys(errorData.errors);
          const errors = [];
          errorsObjectKeys.map((key) => {
            if (Array.isArray(errorData.errors[key])) {
              errors.push(...errorData.errors[key]);
            } else errors.push(errorData.errors[key]);
          });

          return (
            `<strong>${
              errorData.title ||
              "One or more errors occurred while processing the request"
            }</strong>` +
            " </br> - " +
            errors.join("</br> - ")
          );
        } else return errorData.errors as string;
      } 
      else if(Array.isArray(errorData)) {
        // this could be the validation errors
        const errors =  (errorData as any[])
          .filter(e => !!e.errorMessage)
          .map(e => e.errorMessage);
        
          if(errors?.length > 0) {
            return (
              `<strong>${
                "One or more errors occurred while processing the request"
              }</strong>` +
              " </br> - " +
              errors.join("</br> - ")
            );
          }
          else {
            return defaultMessage;
          }
      }
      else if(errorData.errors?.length) {
        return (
          `<strong>${
            err?.message || "One or more errors occurred while processing the request"
          }</strong>` +
          " </br> - " +
          errorData.errors.join("</br> - ")
        );
      }
      else if(typeof err.response.data === "string") {
        return (err.response.data as string);
      }
      else return err?.message || defaultMessage;
    } 
    else return err?.message || defaultMessage;
  };

  return (
    <>
      <ToastContainer style={{ maxWidth: "50%" }} />
      {isAuthenticated && (
        <PageLayout>
          <ProtectedPages />
        </PageLayout>
      )}
      {!isAuthenticated && <UnAuthenticationPages />}
      {globalLoader && <Loader isLoading={globalLoader} />}
    </>
  );
}

function ProtectedPages() {
  const sessionService = SessionService.getInstance();
  const isUserCleared = useAppSelector((state) => state.notificationLogs.isUserCleared);
  const notificationLogsRequestState = useAppSelector((state) => state.notificationLogs.notificationLogRequest);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if(!isUserCleared && !notificationLogsRequestState?.userId) {
        dispatch(updateNotificationLogRequest({
            ...notificationLogsRequestState,
            userId: sessionService.getCurrentUser()?.id,
        }));
    };
  }, []);
  return (
    <Routes>
      <Route element={<ProtectedRoutes />}>
        <Route path="/" element={<Navigate to={sessionService.hasPermission(Permissions.ExecutiveDashboard) ? "/user-dashboard" : "/products"} />} />
        {sessionService.hasPermission(Permissions.ManageProductAssignments) &&
          <Route path="/product-assignment" element={<AssignmentList />} />
        }
        <Route path="/status-reports" element={<StatusReports />} />
        {sessionService.hasPermission(Permissions.ExecutiveDashboard) &&
          <Route path="/executive-dashboard" element={<ExecutiveDashboard />} />}
        {sessionService.hasPermission(Permissions.ProjectOverviewDashboard) &&
          <Route path="/project-dashboard" element={<ProjectDashboard />} />}
          <Route path="/user-dashboard" element={<UserDashboard />} />
        {(sessionService.hasPermission(Permissions.ManageUserRoles) || sessionService.hasPermission(Permissions.ViewUsers)) &&
        <Route path="/roles" element={<RolesList />} />
        }
        {(sessionService.hasPermission(Permissions.CreateUser) || sessionService.hasPermission(Permissions.UpdateUser) || 
        sessionService.hasPermission(Permissions.ViewUsers)) &&
          <Route path="/users" element={<UserList />} />
        }        
        {(sessionService.hasPermission(Permissions.ViewProducts) || sessionService.hasPermission(Permissions.UpdateProduct) 
        || sessionService.hasPermission(Permissions.CreateProduct)) &&
          (<>
            <Route path="/products" element={<ProductList />} />         
            <Route path="/edit-product/:editItemId" element={<EditProduct />} />
          </>)
        }
        {(sessionService.hasPermission(Permissions.UpdateProduct) || sessionService.hasPermission(Permissions.CreateProduct)) &&
          <Route path="/copy-product" element={<EditProduct />} />
        }
        {sessionService.hasPermission(Permissions.ActivateDeactivateProduct) && 
          <Route path="/inactive-products" element={<InactiveProductList />} />
        }

        {(sessionService.hasPermission(Permissions.ManageQuote)
          || sessionService.hasPermission(Permissions.ApproveQuoteCost) || sessionService.hasPermission(Permissions.ApproveDNAndUMAP)
          || sessionService.hasPermission(Permissions.ViewQuoteHistory)) &&
          (<>
            <Route path="/quotes" element={<QuoteList />} />
            <Route path="/edit-quote/:id" element={<CreateUpdateQuote />} />
            <Route path="/create-tooling" element={<CreateUpdateTooling />} />
            <Route path="/toolings" element={<ToolingList />} />
          </>)
        }

        {/* has only view permission */}
        {/* Show quote list, tooling list and tooling details pages */}
        {sessionService.hasPermission(Permissions.ViewQuotes) && !(sessionService.hasPermission(Permissions.ManageQuote)
          || sessionService.hasPermission(Permissions.ApproveQuoteCost) || sessionService.hasPermission(Permissions.ApproveDNAndUMAP)
          || sessionService.hasPermission(Permissions.ViewQuoteHistory)) 
          &&
          (<>
            <Route path="/quotes" element={<QuoteList />} />
            <Route path="/toolings" element={<ToolingList />} />
            <Route path="/create-tooling" element={<CreateUpdateTooling />} />
          </>)
        }

        {sessionService.hasPermission(Permissions.ManageQuote) &&
          <>
            <Route path="/create-quote" element={<CreateUpdateQuote />} />
            <Route path="/bulk-create-quote" element={<BulkCreateQuote />} />
            <Route path="/bulk-update-quote" element={<BulkUpdateQuote />} />
            <Route path="/bulk-create-tooling" element={<BulkCreateTooling />} />
            <Route path="/bulk-update-tooling" element={<BulkUpdateTooling />} />
            <Route path="/create-tooling" element={<CreateUpdateTooling />} />
            <Route path="/toolings" element={<ToolingList />} />
          </>
        }

        {sessionService.hasPermission(Permissions.ManageAttachments) &&
            <Route path="/bulk-upload-attachments" element={<BulkUploadAttachments />} />
        }

        {sessionService.hasPermission(Permissions.CreateProduct) &&
            <Route path="/create-product" element={<CreateProduct />} />
        }
        {sessionService.hasPermission(Permissions.BulkUpdateProducts) &&        
          <Route path="/bulk-update" element={<BulkUpdate />} />
        }
        {sessionService.hasPermission(Permissions.BulkCreateProducts) &&
          <Route path="/bulk-create" element={<BulkUpload />} />
        }
        {(sessionService.hasPermission(Permissions.ManageAppSettings) || sessionService.hasPermission(Permissions.ViewAppSetting)) &&
          (<>
            <Route path="/tag" element={<TagSetting/>} /> 
            <Route path="/project-stage" element={<ProjectStageSetting />} /> 
            <Route path="/project-status" element={<ProjectStatusSetting />} /> 
            {/* <Route path="/project-coordinator" element={<ProjectCoordinatorSetting />} />  */}
            <Route path="/attachment-type" element={<AttachmentTypeSetting />} /> 
            <Route path="/royalty-designer" element={<RoyaltyDesignerSetting />} /> 
            <Route path="/marketing-designer" element={<MarketingDesignerSetting />} /> 
            <Route path="/suppliers" element={<SupplierSetting />} /> 
            <Route path="/finishes" element={<ProductFinishSetting />} /> 
            <Route path="/primary-material" element={<PrimaryMaterialSetting />} /> 
            {/* <Route path="/secondary-material" element={<SecondaryMaterialSetting />} />  */}
            <Route path="/hts-material" element={<HTSMaterialSetting />} /> 
            <Route path="/hts-material-detail" element={<HTSDetailSetting />} /> 
            <Route path="/stockcode-image-hierarchy" element={<StockCodeHierarchySetting />} /> 
            <Route path="/basecode-image-hierarchy" element={<BaseCodeHierarchySetting />} /> 
            <Route path="/category-(and-sort-order)" element={<CategorySetting />} /> 
            <Route path="/manage-cvls" element={<DataFieldTypeSetting />} /> 
            <Route path="/manufacturing-method" element={<ManufactoryMethodSetting />} />
            <Route path="/function" element={<ItemfunctionSetting/> }/>
            <Route path="/sku-type" element={<SkuTypeSetting/> }/>
            <Route path="/style" element={<StyleSetting/>}/>
            <Route path="/series-name" element={<SeriesNameSetting />} />
            <Route path="/pim-field-list" element={<LeagoToPIMFieldList />} />
            <Route path="/export-products-csv-field-sequence" element={<ExportProductsFieldSequence />} />
          </>)
        }
        <Route path="/assignment" element={<Assignment />} />
        <Route path="/notification-log" element={<NotificationLogs />} />
        <Route path="/security-group" element={<SecurityGroup/> }/>
        <Route path="/view-attachment" element={<AttachmentList />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
}

function UnAuthenticationPages() {
  return (
    <Routes>
      <Route path="/aad-login-response" element={<AADLoginResponse />} />
      <Route path="*" element={<Login />} />
    </Routes>
  );
}

export default App;
